import React from "react";
import { icon } from "../../../../../config";

/**
 * UserProfile Sidebar navbar options
 * @author Bhavana
 */
export const userProfileNavbarOptions = [
  {
    label: "Profile",
    icon: icon?.PERSON_SINGLE,
    id: "profile-personal-info",
    section: "personalInfo",
    render: "ShowProfile",
    jumpId: ".0",
    renderCondition: () => {
      return true;
    },
    expandCondition: () => {
      return true; 
    },
    disableCondition: () => {
      return false;
    },
  },
  {
    label: "Login Options",
    icon: icon?.KEY_ROUND,
    id: "profile-login-options",
    section: "loginOptions",
    render: "ShowLoginOptions",
    jumpId: ".1",
    renderCondition: () => {
      // Always show login options
      return true;
    },
    expandCondition: () => {
      // Don't auto-expand by default
      return false;
    },
    disableCondition: () => {
      return false;
    },
  },
  {
    label: "Work Information",
    icon: icon?.WORK,
    id: "profile-work-information",
    section: "workInformation",
    render: "ShowWorkInformation",
    jumpId: ".2",
    renderCondition: () => {
      return true;
    },
    expandCondition: () => {
      return false;
    },
    disableCondition: () => {
      return false;
    },
  },
  {
    label: "My Board Stages",
    icon: icon?.SQUARE_KANBAN,
    id: "profile-board-stages",
    section: "boardStages",
    render: "ShowBoardStages",
    jumpId: ".3",
    renderCondition: () => {
      return true;
    },
    expandCondition: () => {
      return false;
    },
    disableCondition: () => {
      return false;
    },
  },
  {
    label: "Email Notifications",
    icon: icon.MAIL_ICON,
    id: "profile-email-notifications",
    section: "emailNotifications",
    render: "ShowEmailNotifications",
    jumpId: ".4",
    renderCondition: () => {
      return true;
    },
    expandCondition: () => {
      return false;
    },
    disableCondition: () => {
      return false;
    },
  },
  {
    label: "Other Settings",
    icon: icon?.WORKFLOW_23,
    id: "profile-other-settings",
    section: "otherSettings",
    render: "ShowOtherSettings",
    jumpId: ".5",
    renderCondition: () => {
      return true;
    },
    expandCondition: () => {
      return false;
    },
    disableCondition: () => {
      return false;
    },
  },
  {
    label: "Logs",
    icon: icon?.CLOCK_FADING,
    id: "profile-logs",
    section: "logs",
    render: "ShowLogs",
    jumpId: ".6",
    renderCondition: () => {
      return true;
    },
    expandCondition: () => {
      return false;
    },
    disableCondition: () => {
      return false;
    },
  },
];

/**
 * Email Notifications options data
 * Used for Email Notifications section switches
 * @author Bhavana
 */
export const emailNotificationOptions = [
  {
    label: "Email Notifications",
    key: "EmailNotification",
    icon: "null",
    isParent: true,
    description: "Choose what updates you want to receive by email",
  },
  {
    label: "Assigned Tasks",
    key: "AssignedTasks",
    icon: null,
    isParent: false,
    description: "Get notified when a new task is assigned to you",
  },
  {
    label: "Followed Tasks",
    key: "FollowedTasks",
    icon: null,
    isParent: false,
    description: "Get updates on tasks you’re following",
  },
  {
    label: "Daily Digest Updates",
    key: "DailyDigestUpdates",
    icon: null,
    isParent: false,
    description: "Customize what appears in your daily summary emails",
  },
];

/**
 * Email notification settings array structure.
 * apiKey values match getUserEmailControl API response keys for initial values and future save.
 * taskEmails[0]: IsTaskUpdateEnabled, IsApprovalEmailEnabled, IsTaskAssignEmailEnabled, IsCommentEmailEnabled
 * workDigest[0]: IsDailyDigestEnabled, EmailTriggerDuration, ApprovalsPanel, OverdueAssignmentPanel, etc.
 * @author Abhishek Dhillon
 */
export const emailNotificationSettingsArray = [
  {
    section: "eventEmails",
    sectionLabel: "Event Emails",
    sectionDescription: "Receive emails in real-time for each of the following",
    sectionDataSource: "taskEmails",
    sectionEnabledApiKey: "IsTaskUpdateEnabled",
    enabled: true,
    notifications: [
      {
        key: "approvalRequests",
        apiKey: "IsApprovalEmailEnabled",
        label: "Approval Tracking",
        description: "Pending sign off & rejections",
        enabled: true,
      },
      {
        key: "taskAssignments",
        apiKey: "IsTaskAssignEmailEnabled",
        label: "Task Assignments",
        description: "List all new assignments",
        enabled: true,
      },
      {
        key: "directMentions",
        apiKey: "IsCommentEmailEnabled",
        label: "Direct @Mentions (ping)",
        description: "Messages where you were tagged.",
        enabled: true,
      },
    ],
  },
  {
    section: "workDigest",
    sectionLabel: "Work Digest",
    sectionDescription: "Batch Activity Email",
    sectionDataSource: "workDigest",
    sectionEnabledApiKey: "IsDailyDigestEnabled",
    enabled: true,
    frequency: {
      label: "Select Email Frequency",
      description: "",
      key: "emailFrequency",
      apiKey: "EmailTriggerDuration",
      value: 12,
      options: [
        { value: 3, label: "Every 3 hours" },
        { value: 6, label: "Every 6 hours" },
        { value: 12, label: "Twice a day" },
        { value: 24, label: "Once a day" },
      ],
    },
    notifications: [
      {
        key: "approvalRequest",
        apiKey: "ApprovalsPanel",
        label: "Approval Request",
        description: "List of items requiring your sign off",
        enabled: true,
      },
      {
        key: "overdueAssignments",
        apiKey: "OverdueAssignmentPanel",
        label: "Overdue Assignments",
        description: "Shows all overdue tasks",
        enabled: true,
      },
      {
        key: "digestDirectMentions",
        apiKey: "CommentsPanel",
        label: "Direct @Mentions (ping)",
        description: "Messages where you were tagged.",
        enabled: true,
      },
      {
        key: "reminders",
        apiKey: "RemindersPanel",
        label: "Reminders",
        description: "Nudges and reminders you missed",
        enabled: false,
      },
      {
        key: "unseenEmails",
        apiKey: "UnseenEmailPanel",
        label: "Unseen Emails",
        description: "Unread emails in inbox",
        enabled: false,
      },
      {
        key: "streams",
        apiKey: "StreamsPanel",
        label: "Streams",
        description: "Recent updates from followed streams",
        enabled: true,
      },
      {
        key: "followedTaskUpdates",
        apiKey: "FollowedTaskPanel",
        label: "Followed Task Updates",
        description: "Updates on watched tickets",
        enabled: false,
      },
    ],
  },
];